import { reducerWithInitialState } from 'typescript-fsa-reducers'
import { produce } from 'immer'

import { ApiError } from '../coreRoot.reducer'
import {
  checkFileUpload,
  deleteMediaFile,
  getMediaFileMetadata, getUploadedFileAsBase64, listCoverImages,
  listMediaFiles,
  listProfileImages, setMediaFileUsage, setUploadedFileId, setUploadedMediaFile,
  uploadMediaFile,
  uploadTemporaryMediaFile, uploadThumbnailForMediaFile,
} from './images.actions'
import { MediaFileMetadata } from '@3m5/sm_publish-frontend-api'
import { UploadedFile } from '../../api/filescanner/types'

export interface ImagesState {
  fetching: boolean
  settingMediaFileUsage: boolean
  mediaFile?: MediaFileMetadata
  thumbnailForMediaFile?: MediaFileMetadata
  temporaryMediaFile?: MediaFileMetadata
  loadingTMFile?: boolean
  mediaFiles?: MediaFileMetadata[]
  totalMediaFilesCount?: number
  profileImages?: MediaFileMetadata[]
  coverImages?: MediaFileMetadata[]
  uploadedFileId?: string
  uploadedFile?: UploadedFile
  error: ApiError
}

const INITIAL_STATE: ImagesState = {
  fetching: false,
  settingMediaFileUsage: false,
  error: {},
}

export const ImagesReducer = reducerWithInitialState(INITIAL_STATE)
  .case(setUploadedFileId, (state, uploadedFileId) => {
    return ({ ...state, uploadedFileId })
  })
  .case(setUploadedMediaFile, (state, mediaFile) => {
    return ({ ...state, mediaFile })
  })
  /**
   * getMediaFileMetadata
   */
  .case(getMediaFileMetadata.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getMediaFileMetadata
    })
  })

  .case(getMediaFileMetadata.done, (state, { result }) => {
    return produce(state, draft => {
      draft.mediaFile = result
      draft.fetching = false
    })
  })

  .case(getMediaFileMetadata.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getMediaFileMetadata = (error as Response)
    })
  })

  /**
   * uploadMediaFile
   */
  .case(uploadMediaFile.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.uploadMediaFile
    })
  })

  .case(uploadMediaFile.done, (state, { result }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.mediaFile = result
    })
  })

  .case(uploadMediaFile.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.uploadMediaFile = (error as Response)
    })
  })

/**
   * uploadThumbnailForMediaFile
   */
  .case(uploadThumbnailForMediaFile.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.uploadMediaFile
    })
  })

  .case(uploadThumbnailForMediaFile.done, (state, { result }) => {
    return produce(state, draft => {
      const index = state.mediaFiles?.findIndex(item => item.mediaFileId === result.mediaFileId)
      if (index && draft.mediaFiles) {
        draft.mediaFiles[index] = result
      }
      draft.thumbnailForMediaFile = result
    })
  })

  .case(uploadThumbnailForMediaFile.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.uploadMediaFile = (error as Response)
    })
  })

  /**
   * uploadTemporaryMediaFile
   */
  .case(uploadTemporaryMediaFile.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      draft.loadingTMFile = true
      delete draft.error.uploadTemporaryMediaFile
    })
  })

  .case(uploadTemporaryMediaFile.done, (state, { result }) => {
    return produce(state, draft => {
      draft.temporaryMediaFile = result
      draft.fetching = false
      draft.loadingTMFile = result && false
    })
  })

  .case(uploadTemporaryMediaFile.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.loadingTMFile = false
      draft.error.uploadTemporaryMediaFile = (error as Response)
    })
  })

  /**
   * deleteMediaFile
   */
  .case(deleteMediaFile.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.deleteMediaFile
    })
  })

  .case(deleteMediaFile.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
    })
  })

  .case(deleteMediaFile.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.deleteMediaFile = (error as Response)
    })
  })

  /**
   * listMediaFiles
   */
  .case(listMediaFiles.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.listMediaFiles
    })
  })

  .case(listMediaFiles.done, (state, { result }) => {
    return produce(state, draft => {
      // Check if result is an object with items and totalCount properties
      if (result && typeof result === 'object' && 'content' in result && 'totalElements' in result) {
        draft.mediaFiles = result.content
        draft.totalMediaFilesCount = result.totalElements
      }
      draft.fetching = false
    })
  })

  .case(listMediaFiles.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.listMediaFiles = (error as Response)
    })
  })

  /**
   * listProfileImages
   */
  .case(listProfileImages.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.listProfileImages
    })
  })

  .case(listProfileImages.done, (state, { result }) => {
    return produce(state, draft => {
      draft.profileImages = result
      draft.fetching = false
    })
  })

  .case(listProfileImages.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.listProfileImages = (error as Response)
    })
  })

  /**
   * listProfileImages
   */
  .case(listCoverImages.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.listCoverImages
    })
  })

  .case(listCoverImages.done, (state, { result }) => {
    return produce(state, draft => {
      draft.coverImages = result
      draft.fetching = false
    })
  })

  .case(listCoverImages.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.listCoverImages = (error as Response)
    })
  })

  /**
   * setMediaUsage
   */
  .case(setMediaFileUsage.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      draft.settingMediaFileUsage = true
      delete draft.error.uploadMediaFile
    })
  })

  .case(setMediaFileUsage.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.settingMediaFileUsage = false
    })
  })

  .case(setMediaFileUsage.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.settingMediaFileUsage = false
      draft.error.setMediaUsage = (error as Response)
    })
  })

  /**
   * checkFileUpload
   */
  .case(checkFileUpload.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.uploadMediaFile
    })
  })

  .case(checkFileUpload.done, (state, { result }) => {
    return produce(state, draft => {
      draft.uploadedFileId = result.entityId
      draft.fetching = false
    })
  })

  .case(checkFileUpload.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.setMediaUsage = (error as Response)
    })
  })

  /**
   * getUploadedFileAsBase64
   */
  .case(getUploadedFileAsBase64.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.uploadMediaFile
    })
  })
  .case(getUploadedFileAsBase64.done, (state, { result }) => {
    return produce(state, draft => {
      draft.uploadedFile = result
      draft.fetching = false
    })
  })
  .case(getUploadedFileAsBase64.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.setMediaUsage = (error as Response)
    })
  })
